import * as d from 'decoders'
import * as React from 'react'
import * as Api from 'src/api'
import { useApi } from 'src/helpers/hooks'
import { storageValue } from 'src/helpers/storage'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'

const POLL_INTERVAL_MS = 5000
/** The server sends 100 recipients every 5 seconds, so minutes without movement mean the job is stuck. */
const STALL_AFTER_MS = 2 * 60 * 1000

/** The batch whose progress the page shows, kept across reloads and shared between tabs. */
export const bulkMessageBatchStorage = storageValue<string>(
  'app:administratorBulkMessageBatchId',
  (value) => JSON.stringify(value),
  (serializedValue) => d.string.verify(JSON.parse(serializedValue))
)

export type BulkMessageProgress =
  | { readonly status: 'loading' }
  | { readonly status: 'sending' | 'finished'; readonly processed: number; readonly total: number }
  /** Still unfinished, but `processed` has not changed for a while; polling goes on in case it resumes. */
  | { readonly status: 'stalled'; readonly processed: number; readonly total: number }
  /** The batch is unknown or its progress expired (kept for an hour); sending itself is not affected. */
  | { readonly status: 'expired' }

function isFinished(progress: Api.AdministratorBulkMessageProgress): boolean {
  return progress.processed >= progress.total
}

function isNotFound(error: unknown): boolean {
  return typeof error === 'object' && error != null && 'type' in error && error.type === 'NotFoundError'
}

/** Polls every 5 seconds until `processed` reaches `total`; reports `stalled` when it stops moving. */
export function useBulkMessageProgress(batchId: string | null | undefined): BulkMessageProgress | null {
  const headers = useAuthenticatedHeaders()

  const { data, error } = useApi({
    endpoint: Api.getAdministratorBulkMessageProgress,
    params: React.useMemo(() => ({ headers, args: { batchId: batchId ?? '' } }), [headers, batchId]),
    shouldFetch: batchId != null,
    suspense: false,
    refreshInterval: (progress?: Api.AdministratorBulkMessageProgress | null) =>
      progress != null && isFinished(progress) ? 0 : POLL_INTERVAL_MS,
    // Polling pauses while an error is cached and errors are not retried app-wide, so one failed poll would stop it.
    shouldRetryOnError: (pollError: unknown) => !isNotFound(pollError),
  })

  const isSending = data != null && !isFinished(data)
  const processedKey = `${batchId}:${data?.processed}`
  const [stalledKey, setStalledKey] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!isSending) return
    const timer = setTimeout(() => setStalledKey(processedKey), STALL_AFTER_MS)
    return () => clearTimeout(timer)
  }, [isSending, processedKey])

  if (batchId == null) return null
  if (isNotFound(error)) return { status: 'expired' }
  if (data == null) return { status: 'loading' }
  if (isFinished(data)) return { status: 'finished', processed: data.processed, total: data.total }
  return { status: stalledKey === processedKey ? 'stalled' : 'sending', processed: data.processed, total: data.total }
}
